"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Badge,
  Button,
  HStack,
  VStack,
  useToast,
  Flex,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  useDisclosure,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiPlay } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  campaignService,
  type Campaign,
} from "../voice-services/campaignService";
import { useAuth } from "../contexts/AuthContext";
import CreateCampaignModal from "./CreateCampaignModal";
import RunCampaignModal from "./RunCampaignModal";
import { useStableCredits } from "../hooks/useStableCredits";

const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  const { isAdmin, currentClientId, clients } = useAuth();
  const credits = useStableCredits();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isRunOpen,
    onOpen: onRunOpen,
    onClose: onRunClose,
  } = useDisclosure();

  const loadCampaigns = async () => {
    try {
      setIsLoading(true);
      let companyName: string | undefined;

      if (isAdmin && currentClientId) {
        const selectedClient = clients.find(
          (client) => client.id === currentClientId
        );
        companyName = selectedClient?.company_name;
      }

      const data = await campaignService.list(companyName, isAdmin);
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading campaigns:", error);
      setCampaigns([]);
      toast({
        title: "Error loading campaigns",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, [currentClientId, isAdmin, clients]);

  const handleDelete = async (campaignId: string) => {
    try {
      await campaignService.delete(campaignId);
      toast({ title: "Campaign deleted", status: "success", duration: 3000 });
      loadCampaigns();
    } catch (error) {
      toast({
        title: "Error deleting campaign",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleRun = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    onRunOpen();
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  }

  const stats = {
    totalCampaigns: campaigns.length,
    activeCampaigns: campaigns.filter((c) => c.status === "running").length,
    draftCampaigns: campaigns.filter((c) => c.status === "draft").length,
    completedCampaigns: campaigns.filter((c) => c.status === "completed")
      .length,
  };

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold" color="black">
          Outreach Campaigns
        </Text>
        <Button
          variant="brandGradient"
          onClick={onCreateOpen}
          isDisabled={credits !== null && credits < 0.01}
          title={
            credits !== null && credits < 0.01
              ? "Insufficient balance to create campaign"
              : undefined
          }
        >
          Create Campaign
        </Button>
      </Flex>

      <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.600">Total Campaigns</StatLabel>
              <StatNumber color="black">{stats.totalCampaigns}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.600">Active</StatLabel>
              <StatNumber color="green.500">{stats.activeCampaigns}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.600">Draft</StatLabel>
              <StatNumber color="yellow.500">{stats.draftCampaigns}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.600">Completed</StatLabel>
              <StatNumber color="blue.500">
                {stats.completedCampaigns}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </Grid>

      <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6}>
        {campaigns.map((campaign) => (
          // disable this card click if credits are less than 1
          <Card
            key={campaign.campaign_id}
            borderLeft="4px"
            borderLeftColor="primary.500"
            cursor={credits !== null && credits < 0.01 ? "not-allowed" : "pointer"}
            onClick={() => {
              if (credits !== null && credits < 0.01) return; // block click
              navigate(`/campaigns/${campaign.campaign_id}`);
            }}
            _hover={credits !== null && credits < 0.01 ? {} : { shadow: "md" }}
            opacity={credits !== null && credits < 0.01 ? 0.5 : 1}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to edit campaign"
                : undefined
            }
          >
            <CardHeader>
              <Flex justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Text fontWeight="bold" fontSize="lg" color="black">
                      {campaign.name}
                    </Text>
                    <Badge
                      colorScheme={
                        campaign.status === "running"
                          ? "green"
                          : campaign.status === "draft"
                          ? "yellow"
                          : "blue"
                      }
                    >
                      {campaign.status.toUpperCase()}
                    </Badge>
                  </HStack>
                </VStack>
                <HStack onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="green"
                    onClick={() => handleRun(campaign)}
                    isDisabled={credits !== null && credits < 0.01}
                    title={
                      credits !== null && credits < 0.01
                        ? "Insufficient balance to run campaign"
                        : undefined
                    }
                  >
                    <FiPlay />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate(`/campaigns/${campaign.campaign_id}`)
                    }
                    isDisabled={credits !== null && credits < 0.01}
                    title={
                      credits !== null && credits < 0.01
                        ? "Insufficient balance to edit campaign"
                        : undefined
                    }
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDelete(campaign.campaign_id)}
                  >
                    <DeleteIcon />
                  </Button>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  Agents:{" "}
                  {[
                    campaign.voice_agent_id && "Voice",
                    campaign.sms_agent_id && "SMS",
                    campaign.email_agent_id && "Email",
                    campaign.whatsapp_agent_id && "WhatsApp",
                  ]
                    .filter(Boolean)
                    .join(", ") || "None configured"}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Created: {new Date(campaign.created_at).toLocaleDateString()}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        ))}
      </Grid>

      <CreateCampaignModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSuccess={loadCampaigns}
      />
      <RunCampaignModal
        isOpen={isRunOpen}
        onClose={onRunClose}
        campaign={selectedCampaign}
      />
    </Box>
  );
};

export default CampaignsPage;
